import * as Switch from '@radix-ui/react-switch';
import { Text } from '@ui/index';
import { startCase } from 'lodash-es';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components/macro';

import { useFeatureFlags } from '@src/hooks/useFeatureFlags';
import { SettableFeatureFlags } from '@src/types';

const StyledSwitchRoot = styled(Switch.Root)`
  width: 42px;
  height: 25px;
  background-color: var(--color-gray-1);
  border-radius: 9999px;
  position: relative;
  /* box-shadow: 0 2px 10px black; */
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  &[data-state='checked'] {
    background-color: var(--color-navy-3);
  }
`;

const StyledSwitchThumb = styled(Switch.Thumb)`
  display: block;
  width: 20px;
  height: 20px;
  background-color: var(--color-primary);
  border-radius: 9999px;
  /* box-shadow: 0 2px 2px black; */
  transition: transform 100ms;
  transform: translateX(-9px);
  will-change: transform;
  &[data-state='checked'] {
    transform: translateX(12px);
  }
  &[data-disabled] {
    background-color: var(--color-gray-5);
  }
`;

const FlagToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const TextGrow = styled(Text)`
  flex-grow: 1;
`;

type FeatureFlagToggleProps = {
  flag: string;
  state: boolean;
  setFlagState: (update: boolean) => void;
};

const FeatureFlagToggle = ({ flag, state, setFlagState }: FeatureFlagToggleProps) => (
  <FlagToggleContainer>
    <TextGrow>{startCase(flag)}</TextGrow>
    <StyledSwitchRoot id={flag} checked={state} onCheckedChange={(c) => setFlagState(c)}>
      <StyledSwitchThumb />
    </StyledSwitchRoot>
  </FlagToggleContainer>
);

const show = () =>
  keyframes({
    from: {
      opacity: 0,
      transform: 'translate3d(0, -20px, 0)',
    },
    to: {
      opacity: 1,
      transform: 'translate3d(0, 0, 0)',
    },
  });

const hide = () =>
  keyframes({
    from: {
      opacity: 1,
      transform: 'translate3d(0, 0, 0)',
    },
    to: {
      opacity: 0,
      transform: 'translate3d(0, -20px, 0)',
    },
  });

const StyledDialog = styled.dialog`
  background-color: var(--color-white);
  color: var(--color-navy-1);
  overflow-y: auto;
  padding: 2rem;
  max-width: 28rem;
  &[open] {
    animation: ${show()} 250ms ease-in-out;
  }
  &.modal-closing {
    animation: ${hide()} 250ms ease-in-out;
  }
`;

const DialogContents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SettingContainer = styled.div<{ state: 'idle' | 'reloading' }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  ${({ state }) =>
    state === 'reloading' &&
    css`
      color: var(--color-gray-1);
    `}
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

const QA_FLAGS = ['requestPreReleaseData'];

export const FeatureFlagSettings = ({ open, onClose: onCloseHandler }: Props) => {
  const [dialogState, setDialogState] = useState<'open' | 'closing' | 'closed'>('closed');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { values, setters } = useFeatureFlags();

  useMemo(() => {
    if (open && !dialogRef?.current?.open) {
      dialogRef?.current?.showModal();
      setDialogState('open');
    }
  }, [open]);

  const onCloseFinish = useCallback(() => {
    dialogRef?.current?.close();
    setDialogState('closed');
  }, []);

  const onClose = useCallback(() => {
    onCloseHandler();
    setDialogState('closing');
    setTimeout(() => {
      onCloseFinish();
    }, 250);
  }, [onCloseFinish, onCloseHandler]);

  const onEsc = useCallback(
    (e: ChangeEvent<HTMLDialogElement>) => {
      e.preventDefault();
      onClose();
    },
    [onClose],
  );

  const onClick = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: TODO: fix
    ({ target }: any) => {
      const { current: el } = dialogRef;
      if (target === el) {
        onClose();
      }
    },
    [onClose],
  );

  const qaFlags = Object.entries(setters).filter(([flagId]) => QA_FLAGS.includes(flagId));

  return (
    <StyledDialog
      aria-label="Settings"
      id="settingsDialog"
      ref={dialogRef}
      onClick={onClick}
      onCancel={onEsc}
      className={dialogState === 'closing' ? 'modal-closing' : undefined}
    >
      <DialogContents>
        <Text variant="page-title" tag="h2">
          QA mode
        </Text>
        <SettingContainer state="idle">
          {qaFlags.map(([flagId, setter]) => (
            <FeatureFlagToggle
              key={flagId}
              flag={flagId}
              state={values?.[flagId as keyof SettableFeatureFlags] ?? false}
              setFlagState={setter}
            />
          ))}
        </SettingContainer>
      </DialogContents>
    </StyledDialog>
  );
};
